import type { ReactNode } from "react";
import { CheckCircleIcon, InfoIcon, WarningIcon } from "./icons.tsx";

/** 画面の見出しと説明 */
export function PageHeader({ title, description }: { title: string; description: ReactNode }) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      <p>{description}</p>
    </div>
  );
}

/** すりガラスのカード */
export function Card({
  children,
  labelledBy,
  label,
}: {
  children: ReactNode;
  labelledBy?: string;
  label?: string;
}) {
  return (
    <section className="card" aria-labelledby={labelledBy} aria-label={label}>
      {children}
    </section>
  );
}

/** 見出しの左に置く四角いアイコン */
export function SectionIcon({ children }: { children: ReactNode }) {
  return (
    <span className="section-icon" aria-hidden="true">
      {children}
    </span>
  );
}

/** 補足（info: 青、caution: 黄、warning: 赤） */
export function Note({
  tone = "info",
  children,
}: {
  tone?: "info" | "caution" | "warning";
  children: ReactNode;
}) {
  return (
    <div className={`note note--${tone}`} role="note">
      {tone === "info" ? <InfoIcon /> : <WarningIcon />}
      <span>{children}</span>
    </div>
  );
}

/** 操作の結果。success は「保存しました。」などの完了、error は失敗 */
export type Status = { tone: "success" | "error"; text: string } | null;

export function StatusMessage({ status }: { status: Status }) {
  // 読み上げのため、内容が無いときも領域は置いておく
  return (
    <p className={`status-message status-message--${status?.tone ?? "none"}`} role="status">
      {status?.tone === "success" ? <CheckCircleIcon size={16} /> : null}
      {status?.tone === "error" ? <WarningIcon size={16} /> : null}
      <span>{status?.text}</span>
    </p>
  );
}

/** 読み込み中・失敗の表示 */
export function LoadState({
  state,
  onRetry,
}: {
  state: "loading" | "error";
  onRetry?: () => void;
}) {
  if (state === "loading") {
    return (
      <p className="load-state" role="status">
        読み込んでいます…
      </p>
    );
  }
  return (
    <div className="load-state" role="alert">
      <p>読み込めませんでした。時間をおいて再度お試しください。</p>
      {onRetry ? (
        <button type="button" className="button button--secondary" onClick={onRetry}>
          再読み込み
        </button>
      ) : null}
    </div>
  );
}
